import { ATTRIBUTE_KEY_PATTERN, ATTRIBUTE_LIMITS, type AttributeObject, type AttributeType } from "@sagot/constants";
import type { AttributeDefinition, AttributeInput } from "../shared";

/** What the create / edit dialog holds while it is open. */
export interface AttributeDraft {
  object: AttributeObject;
  key: string;
  label: string;
  type: AttributeType;
  /** One option per line (only a `list` attribute keeps them). */
  options: string;
  /** "AI can see this" (PDI-007): only a visitor attribute carries it; off until a member turns it on. */
  aiVisible: boolean;
}

/** Why the "AI can see this" switch cannot simply be turned on: personal data stays off by default, a hidden field is locked off. */
export type AiVisibleLock = "personal" | "hidden" | null;

export type AttributeProblem = "label" | "key" | "options" | "optionLength";
export type AttributeProblems = Partial<Record<AttributeProblem, true>>;

export const emptyDraft = (): AttributeDraft => ({ object: "visitor", key: "", label: "", type: "text", options: "", aiVisible: false });

/** `hidden` wins over `personal`: a field hidden from the assistant can never be shown to it. */
export const aiVisibleLock = (definition: Pick<AttributeDefinition, "pii" | "sensitive_hidden_from_ai">): AiVisibleLock => (definition.sensitive_hidden_from_ai ? "hidden" : definition.pii ? "personal" : null);

export const draftOf = (definition: AttributeDefinition): AttributeDraft => ({
  object: definition.object,
  key: definition.key,
  label: definition.label,
  type: definition.type,
  options: (definition.options ?? []).join("\n"),
  aiVisible: (definition.ai_visible ?? false) && !definition.sensitive_hidden_from_ai,
});

/** `Grade level` becomes `grade_level`: lower case, `_` between words, starts with a letter, at most 40 characters. */
export function slugKey(label: string): string {
  const slug = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const lettered = /^[a-z]/.test(slug) ? slug : slug ? `a_${slug}` : "";
  return lettered.slice(0, ATTRIBUTE_LIMITS.key).replace(/_+$/, "");
}

/** The options typed one per line: trimmed, blanks dropped, duplicates removed (the first spelling wins). */
export function parseOptions(text: string): string[] {
  const seen = new Set<string>();
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => {
      const fold = line.toLowerCase();
      if (!line || seen.has(fold)) return false;
      seen.add(fold);
      return true;
    });
}

export function problemsOf(draft: AttributeDraft): AttributeProblems {
  const problems: AttributeProblems = {};
  if (!draft.label.trim()) problems.label = true;
  if (!ATTRIBUTE_KEY_PATTERN.test(draft.key)) problems.key = true;
  if (draft.type === "list") {
    const options = parseOptions(draft.options);
    if (options.length === 0 || options.length > ATTRIBUTE_LIMITS.options) problems.options = true;
    if (options.some((option) => option.length > ATTRIBUTE_LIMITS.option)) problems.optionLength = true;
  }
  return problems;
}

export const hasProblems = (problems: AttributeProblems): boolean => Object.keys(problems).length > 0;

/** The request body of a valid draft. */
export function inputOf(draft: AttributeDraft): AttributeInput {
  return {
    object: draft.object,
    key: draft.key,
    label: draft.label.trim(),
    type: draft.type,
    options: draft.type === "list" ? parseOptions(draft.options) : null,
    ai_visible: draft.object === "visitor" && draft.aiVisible,
  };
}
